/**
 * The toon/painterly material — the art direction, as a shader.
 *
 * Spec §2: "3-band toon ramp with a hand-painted gradient texture, not a hard
 * cel step. Warm bounce light in shadows." Every number here comes from
 * `src/render/palette.ts`; none is restated. The mood board sheet draws its
 * "toon ramp →" bar from the same constants, so the sheet and the game cannot
 * drift (CLAUDE.md: render the source of truth, do not copy it).
 *
 * ── Why this is a ShaderMaterial and not MeshToonMaterial ─────────────────────
 * Three ships `MeshToonMaterial`, and it would give the banding. It would not
 * give the three things that actually carry the look:
 *
 *   1. **Warm bounce.** The shadow band is not the key light dimmed; it is the
 *      zone's own FILL hue. `docs/art-direction.md` calls this "most of what
 *      separates painterly from flat vertex colour with an outline," and it is a
 *      per-zone colour, not a global ambient.
 *   2. **Wrapped light for fabric.** Pile scatters, so light leaves a plush
 *      surface well past the geometric terminator. Without this Rosalie has the
 *      same hard terminator as a concrete kerb, and she stops reading as a toy —
 *      which is the whole argument of the game (D-002).
 *   3. **One shared zone state.** `applyZone()` updates ONE set of uniform
 *      objects and every material in the scene follows, because they hold the
 *      same objects by reference. A grade that has to be pushed into thirty
 *      materials is a grade that will be wrong in one of them.
 *
 * ── The band structure, in one paragraph ──────────────────────────────────────
 * Accumulate real irradiance from the scene's lights (so any lighting rig works
 * and a red lamp still reads red), reduce it to a single 0..1 exposure `t`,
 * and use `t` to pick between three authored LIGHT colours — not three
 * brightnesses. Multiply the surface albedo by that. The bands therefore carry
 * hue, which is what makes a shadow warm rather than merely dark.
 */

import * as THREE from 'three';
import { ROSALIE, TOON_RAMP, type ZonePalette } from '@/render/palette';

/**
 * `wet` is mineral that has been rained on, and it is a fifth kind rather than a
 * lever because there is no lever.
 *
 * Beat 6 is the first exterior and the yard is a wet concrete apron in a storm.
 * The obvious implementation — darker albedo plus a raised glint on `mineral` —
 * is not available: `SurfaceOptions` carries only the seam, and `glint` is fixed
 * PER KIND in the `SURFACE` table below. There is no call-site control at all.
 *
 * That leaves two honest options and one of them is wrong. Raising `mineral`'s
 * glint would make every concrete floor in the game wet, including four rooms
 * she has already walked through. So: a fifth kind, one row, reusable by beat
 * 7's ditch and beat 12's dawn yard.
 */
export type SurfaceKind = 'fabric' | 'metal' | 'mineral' | 'organic' | 'wet';

/**
 * How each surface kind bends the shared lighting model.
 *
 * These are the only per-material knobs. Everything else — thresholds, softness,
 * band colours — is zone state and is shared, because a scene where two objects
 * disagree about where the terminator sits does not read as one drawing.
 */
interface SurfaceParams {
  /**
   * Light wrap, 0..1. How far past the geometric terminator light still reaches.
   *
   * This is the pile depth expressed as a shading term. Fabric is high because
   * that is physically what a plush surface does; concrete is nearly zero.
   */
  wrap: number;
  /**
   * Rim scatter, 0..1. Light passing THROUGH the pile at glancing angles.
   *
   * On fabric this is the single strongest "soft" cue available — stronger than
   * colour, stronger than silhouette at gameplay distance. It is deliberately
   * gated on how lit the pixel already is: a rim that glows in the dark is a
   * video-game effect and would undo everything else here.
   */
  fuzz: number;
  /**
   * Hard-edged specular blob, 0..1. Galvanized pipe and stainless troughs.
   *
   * Not a smooth Blinn lobe — a smoothstepped blob, because a soft highlight in
   * a banded image reads as a rendering mistake rather than as a shine.
   */
  glint: number;
  /** Whether the wear parameter (D-002) applies. Only she wears. */
  wearable: number;
  /**
   * Whether the ROOM's grime may bleed rust into this surface. Steel only.
   *
   * Separate from `glint` even though metal is the only kind with either, and
   * deliberately so: the alternative is testing `uGlint > 0.3` in the fragment
   * shader, which couples "is this steel" to a lighting number that exists to be
   * tuned. The first time somebody dulls the glint on galvanized pipe the rust
   * would silently disappear with it.
   */
  rustable: number;
}

const SURFACE: Record<SurfaceKind, SurfaceParams> = {
  fabric: { wrap: 0.45, fuzz: 0.55, glint: 0.0, wearable: 1, rustable: 0 },
  metal: { wrap: 0.02, fuzz: 0.06, glint: 0.65, wearable: 0, rustable: 1 },
  mineral: { wrap: 0.12, fuzz: 0.0, glint: 0.0, wearable: 0, rustable: 0 },
  organic: { wrap: 0.25, fuzz: 0.14, glint: 0.05, wearable: 0, rustable: 0 },
  // Mineral's wrap, tightened, with a real glint. 0.42 sits deliberately below
  // metal's 0.65: wet concrete catches a headlight, galvanized steel throws it
  // back. `rustable` stays 0 — a wet floor is not steel, and the room's grime
  // has no business bleeding rust into standing water.
  wet: { wrap: 0.10, fuzz: 0.0, glint: 0.42, wearable: 0, rustable: 0 },
};

/**
 * Zone state, shared BY REFERENCE across every toon material in the scene.
 *
 * Uniform objects, not values: each material's `uniforms` map points at these
 * same objects, so writing `zoneUniforms.uKey.value.set(...)` reaches all of
 * them in one assignment. This is what makes a zone transition atomic.
 */
const zoneUniforms = {
  uKey: { value: new THREE.Color(0xffffff) },
  uFill: { value: new THREE.Color(0x808080) },
  uShadowCol: { value: new THREE.Color(0x202020) },
  uThresholds: { value: new THREE.Vector2(TOON_RAMP.thresholds[0], TOON_RAMP.thresholds[1]) },
  uSoftness: { value: new THREE.Vector2(TOON_RAMP.softness[0], TOON_RAMP.softness[1]) },
  uShadowFillMix: { value: TOON_RAMP.shadowFillMix },
  uHighlightDesat: { value: TOON_RAMP.highlightDesaturation },
  /**
   * The irradiance luminance that counts as "fully lit", i.e. the top of the
   * ramp.
   *
   * Exists so the ramp is anchored to the ART, not to whatever intensities the
   * lighting rig happens to use. Without it, adding a second fixture to a room
   * silently pushes every surface up a band and the zone's value structure —
   * the thing the whole palette is built on — quietly changes. The lighting rig
   * sets this; see `src/render/lighting.ts`.
   */
  uExposureRef: { value: 1.0 },
  /**
   * D-002's wear parameter: 0 at Born, 1 by Act 3, monotonic, never reset.
   * "A pristine toy is a product; a worn toy is grief."
   */
  uWear: { value: 0 },
  uWearTint: { value: new THREE.Color(ROSALIE.wearTint) },
  /**
   * How filthy the ROOM is, 0..1. A property of a place, not of the story.
   *
   * Kept strictly apart from `uWear` above, which is D-002's monotonic story
   * progress and belongs to Rosalie. Both dirty things and it would have been
   * one uniform if they meant the same thing — they do not: hers only ever rises
   * and is the same in every room, this one is per zone and does not change.
   * Collapsing them would mean the fattening hall could age her by being dirty.
   */
  uGrime: { value: 0 },
  uGrimeColour: { value: new THREE.Color(0x000000) },
  uRustColour: { value: new THREE.Color(0x000000) },
  /**
   * The zone's haze. **Ours, not three's `scene.fog`** — see the note below.
   */
  uFogColour: { value: new THREE.Color(0x000000) },
  uFogDensity: { value: 0 },
};

/** Optional per-surface extras. */
export interface SurfaceOptions {
  /**
   * A felt seam down the object's local X = 0, on its upper half (spec §2).
   *
   * Object space on purpose. Rosalie's torso is ONE lofted surface (the G1 fix),
   * so its local midline IS her spine — no UVs, no texture, and it follows the
   * mesh if she is ever reproportioned. Only meaningful on that torso; on a
   * limb it would draw a stripe down a leg.
   */
  seam?: boolean;
  /** Seam half-width in metres. */
  seamWidth?: number;
  /**
   * Thread colour. Defaults to Rosalie's.
   *
   * Exists because her mother is the same mesh in an older dye and her seams have
   * been strained — a detail that is carried entirely by this one value, and that
   * would otherwise have needed a second shader or a global that changed her
   * stitching too.
   */
  seamColour?: number;
  /**
   * Self-lit: `albedo × glow` is added after the bands, whatever the lights
   * do. 0 or absent is an ordinary surface.
   *
   * For a lit space seen from a darker one, which the bands cannot draw: they
   * map irradiance to three authored colours, so a surface only gets brighter
   * than the room by being lit harder, and the only way to light it harder is
   * a point light. In the farrowing hall that is a seventh, about 1.3 ms a
   * frame in a hall already over its frame target (`docs/performance.md`).
   * Beat 2's vestibule, the gap behind the ajar walkway door, was the first
   * need: painted white it still rendered mid-grey (Alex chose this over the
   * light, beat 1 revision 2 step 3). Not a bloom source by itself; whatever
   * the post stack does with bright pixels it does with these.
   */
  glow?: number;
}

const DEFAULT_SEAM_WIDTH = 0.006;

/** Every live toon material, so a program-level change can reach all of them. */
const live = new Set<THREE.ShaderMaterial>();

// ── No backticks inside these two strings, not even in a comment ──────────────
// They are template literals, so a backtick ends the shader mid-sentence and
// the rest of the GLSL is parsed as TypeScript. The error you get names a line
// in the comment and says "expected a semicolon", which is true and completely
// unhelpful. This cost three round trips before it was written down.
const VERTEX = /* glsl */ `
  // shadowmap_vertex offsets the shadow lookup along the world normal to fight
  // acne, and only does so when this is defined. Without it every surface in the
  // room gets the fallback zero normal and shadow acne comes back as a moiré
  // that looks exactly like a broken ramp.
  #define HAS_NORMAL

  #include <common>
  #include <shadowmap_pars_vertex>

  varying vec3 vNormalView;
  varying vec3 vViewPos;
  varying vec3 vObjectPos;
  varying vec3 vObjectNormal;
  varying vec3 vWorldPos;
  varying vec3 vWorldNormal;

  void main() {
    // The ASSET's own space, before instancing. The felt seam is drawn against
    // this, and it must stay the space the mesh was authored in — an instanced
    // copy of a seamed mesh would otherwise get its seam in a different place.
    // (Nothing seamed is instanced today. It will be.)
    vObjectPos = position;
    vObjectNormal = normal;

    vec3 objectNormal = normal;
    vec3 transformed = position;

    // Instancing, applied in OBJECT space and before everything else, exactly
    // as three's own project_vertex and defaultnormal_vertex chunks do it.
    //
    // This was missing from the first version of this shader, and the failure is
    // worth recording because it looks like nothing to do with a shader: every
    // instance of an InstancedMesh rendered **at the same place**, so a hall
    // dressed with sixteen farrowing crates showed exactly one, stacked on top
    // of the door. The instance matrices were all correct in the buffer — they
    // simply were never read, because a hand-written vertex shader gets none of
    // three's instancing support for free.
    #ifdef USE_INSTANCING
      mat3 instanceNormalMatrix = mat3( instanceMatrix );
      objectNormal /= vec3(
        dot( instanceNormalMatrix[ 0 ], instanceNormalMatrix[ 0 ] ),
        dot( instanceNormalMatrix[ 1 ], instanceNormalMatrix[ 1 ] ),
        dot( instanceNormalMatrix[ 2 ], instanceNormalMatrix[ 2 ] )
      );
      objectNormal = instanceNormalMatrix * objectNormal;
      transformed = ( instanceMatrix * vec4( transformed, 1.0 ) ).xyz;
    #endif

    vec3 transformedNormal = normalMatrix * objectNormal;
    vNormalView = transformedNormal;

    vec4 mvPosition = modelViewMatrix * vec4( transformed, 1.0 );
    vViewPos = mvPosition.xyz;
    gl_Position = projectionMatrix * mvPosition;

    vec4 worldPosition = modelMatrix * vec4( transformed, 1.0 );

    // World space, for the room's grime. It has to be world and not object
    // space: a slurry stain that stops at the joint between the wall mesh and
    // the kerb mesh is not a stain, it is a decal, and the whole reason this is
    // done in the shader (D-003's argument, one level out) is that the building
    // is made of thirty boxes which must share one field of dirt.
    vWorldPos = worldPosition.xyz;
    // NOT normalMatrix * objectNormal — that is the VIEW-space normal, and it
    // swings with the camera. Grime cares which way is up in the world.
    vWorldNormal = mat3( modelMatrix ) * objectNormal;

    #include <shadowmap_vertex>
  }
`;

const FRAGMENT = /* glsl */ `
  #include <common>
  #include <packing>
  #include <lights_pars_begin>
  #include <shadowmap_pars_fragment>

  uniform vec3 uFogColour;
  uniform float uFogDensity;

  uniform vec3 uDiffuse;
  uniform float uWrap;
  uniform float uFuzz;
  uniform float uGlint;
  uniform float uWearable;
  uniform float uSeamWidth;
  uniform vec3 uSeamColour;
  uniform float uGlow;

  uniform vec3 uKey;
  uniform vec3 uFill;
  uniform vec3 uShadowCol;
  uniform vec2 uThresholds;
  uniform vec2 uSoftness;
  uniform float uShadowFillMix;
  uniform float uHighlightDesat;
  uniform float uExposureRef;
  uniform float uWear;
  uniform vec3 uWearTint;

  uniform float uGrime;
  uniform vec3 uGrimeColour;
  uniform vec3 uRustColour;
  uniform float uRustable;

  varying vec3 vNormalView;
  varying vec3 vViewPos;
  varying vec3 vObjectPos;
  varying vec3 vObjectNormal;
  varying vec3 vWorldPos;
  varying vec3 vWorldNormal;

  float lum( vec3 c ) { return dot( c, vec3( 0.2126, 0.7152, 0.0722 ) ); }

  // ── The dirt field ──────────────────────────────────────────────────────────
  //
  // Value noise, sampled in world space. Deliberately not a texture: there are no
  // UVs anywhere in this project (every asset is procedural, and the felt seam is
  // drawn from object coordinates for the same reason), and a room built from
  // thirty boxes needs its stains to cross the joints between them.
  //
  // Cheap on purpose. D-027 found this hardware is bound by lights per fragment,
  // so a fragment cost measured in a dozen multiplies is affordable and a
  // four-octave fBm is not.
  float grimeHash( vec3 p ) {
    p = fract( p * 0.3183099 + vec3( 0.1, 0.2, 0.3 ) );
    p *= 17.0;
    return fract( p.x * p.y * p.z * ( p.x + p.y + p.z ) );
  }

  float grimeNoise( vec3 p ) {
    vec3 i = floor( p );
    vec3 f = fract( p );
    f = f * f * ( 3.0 - 2.0 * f );
    float n000 = grimeHash( i );
    float n100 = grimeHash( i + vec3( 1.0, 0.0, 0.0 ) );
    float n010 = grimeHash( i + vec3( 0.0, 1.0, 0.0 ) );
    float n110 = grimeHash( i + vec3( 1.0, 1.0, 0.0 ) );
    float n001 = grimeHash( i + vec3( 0.0, 0.0, 1.0 ) );
    float n101 = grimeHash( i + vec3( 1.0, 0.0, 1.0 ) );
    float n011 = grimeHash( i + vec3( 0.0, 1.0, 1.0 ) );
    float n111 = grimeHash( i + vec3( 1.0, 1.0, 1.0 ) );
    return mix(
      mix( mix( n000, n100, f.x ), mix( n010, n110, f.x ), f.y ),
      mix( mix( n001, n101, f.x ), mix( n011, n111, f.x ), f.y ),
      f.z
    );
  }

  void main() {
    vec3 N = normalize( vNormalView );
    // Thin plates — floor slats, fan shutters, the door's kick plate — are
    // single-sided geometry seen from both sides. Without this their back faces
    // land permanently in the shadow band and read as holes in the floor.
    if ( ! gl_FrontFacing ) N = -N;
    vec3 V = normalize( -vViewPos );

    vec3 irradiance = vec3( 0.0 );
    // Direction of whatever is lighting this pixel hardest, accumulated by
    // contribution. The glint needs a single direction and the scene has
    // several lights; the brightest one is the only one a highlight would
    // plausibly come from in a drawing.
    vec3 dominant = vec3( 0.0 );

    // Declared OUTSIDE the light loops, which is not a style choice.
    // The unroll pragma textually repeats the loop body, so a declaration
    // inside it becomes a redefinition on the second light and
    // the fragment shader fails to compile — with a WebGL error naming a line
    // number in generated code that does not exist in this file. Three's own
    // lights_fragment_begin hoists its scratch for exactly this reason.
    float lightAmount;
    float shadowAmount;
    vec3 contribution;

    #if NUM_DIR_LIGHTS > 0
      DirectionalLight directionalLight;
      #if defined( USE_SHADOWMAP ) && NUM_DIR_LIGHT_SHADOWS > 0
        DirectionalLightShadow directionalLightShadow;
      #endif
      #pragma unroll_loop_start
      for ( int i = 0; i < NUM_DIR_LIGHTS; i ++ ) {
        directionalLight = directionalLights[ i ];
        // Wrapped lambert. See SurfaceParams.wrap — this is the pile.
        lightAmount = ( dot( N, directionalLight.direction ) + uWrap ) / ( 1.0 + uWrap );
        lightAmount = max( lightAmount, 0.0 );
        shadowAmount = 1.0;
        // receiveShadow is a per-object uniform three sets for us, and three's
        // own lights chunk gates every lookup on it. Omitting it meant a mesh
        // told explicitly not to receive shadows was darkened anyway — the light
        // fittings and the ceiling, which is where it showed. Found on review.
        #if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_DIR_LIGHT_SHADOWS )
          directionalLightShadow = directionalLightShadows[ i ];
          shadowAmount = receiveShadow ? getShadow(
            directionalShadowMap[ i ],
            directionalLightShadow.shadowMapSize,
            directionalLightShadow.shadowIntensity,
            directionalLightShadow.shadowBias,
            directionalLightShadow.shadowRadius,
            vDirectionalShadowCoord[ i ]
          ) : 1.0;
        #endif
        contribution = directionalLight.color * lightAmount * shadowAmount;
        irradiance += contribution;
        dominant += directionalLight.direction * lum( contribution );
      }
      #pragma unroll_loop_end
    #endif

    #if NUM_POINT_LIGHTS > 0
      PointLight pointLight;
      IncidentLight pointDirect;
      #if defined( USE_SHADOWMAP ) && NUM_POINT_LIGHT_SHADOWS > 0
        PointLightShadow pointLightShadow;
      #endif
      #pragma unroll_loop_start
      for ( int i = 0; i < NUM_POINT_LIGHTS; i ++ ) {
        pointLight = pointLights[ i ];
        getPointLightInfo( pointLight, vViewPos, pointDirect );
        lightAmount = ( dot( N, pointDirect.direction ) + uWrap ) / ( 1.0 + uWrap );
        lightAmount = max( lightAmount, 0.0 );
        shadowAmount = 1.0;
        #if defined( USE_SHADOWMAP ) && ( UNROLLED_LOOP_INDEX < NUM_POINT_LIGHT_SHADOWS )
          pointLightShadow = pointLightShadows[ i ];
          shadowAmount = receiveShadow ? getPointShadow(
            pointShadowMap[ i ],
            pointLightShadow.shadowMapSize,
            pointLightShadow.shadowIntensity,
            pointLightShadow.shadowBias,
            pointLightShadow.shadowRadius,
            vPointShadowCoord[ i ],
            pointLightShadow.shadowCameraNear,
            pointLightShadow.shadowCameraFar
          ) : 1.0;
        #endif
        contribution = pointDirect.color * lightAmount * shadowAmount;
        irradiance += contribution;
        dominant += pointDirect.direction * lum( contribution );
      }
      #pragma unroll_loop_end
    #endif

    // ── Spot lights, and why this block had to exist at all ────────────────
    //
    // 'Fixture' gained a 'spot' kind for beat 11's desk lamp (D-187 needs a lamp
    // under Kestner's eyeline, and a POINT light lights a face from below as
    // readily as from above). The backlog priced that as "one fixture kind, its
    // shadow handling, and a gate" — and it was wrong, because THIS shader is
    // not 'MeshToonMaterial'. It loops the light types by hand, it had exactly
    // two of them, and a 'THREE.SpotLight' in a scene lit by it therefore
    // contributed NOTHING: three uploaded the uniforms and nobody read them.
    // The symptom is a room that renders perfectly and is simply not lit by the
    // lamp you added, which is indistinguishable from an intensity that is too
    // low. Found by probing an absurd cone angle and getting a byte-identical
    // frame ('docs/traps.md').
    //
    // **No shadow block, deliberately.** Nothing here but the key casts — see
    // 'lighting.ts''s note on why an interior gets one shadow-casting
    // directional — and a spot built by 'createZoneLighting' never sets
    // 'castShadow'. If one ever must, this needs 'spotLightShadows',
    // 'spotShadowMap' and 'vSpotLightCoord[ i ]', mirroring the point block
    // above; it will not appear on its own.
    //
    // It costs nothing in rooms without a spot: 'NUM_SPOT_LIGHTS' is 0 there and
    // the preprocessor removes the whole block. In the office it replaces the
    // point light rather than joining it, so D-027's per-fragment light count is
    // unmoved — which is the only reason this was affordable.
    #if NUM_SPOT_LIGHTS > 0
      SpotLight spotLight;
      IncidentLight spotDirect;
      #pragma unroll_loop_start
      for ( int i = 0; i < NUM_SPOT_LIGHTS; i ++ ) {
        spotLight = spotLights[ i ];
        // 'getSpotLightInfo' folds the cone and the distance falloff into
        // 'color' and zeroes it outside the cone, so 'visible' needs no test —
        // the same reason the point loop above does not read it either.
        getSpotLightInfo( spotLight, vViewPos, spotDirect );
        lightAmount = ( dot( N, spotDirect.direction ) + uWrap ) / ( 1.0 + uWrap );
        lightAmount = max( lightAmount, 0.0 );
        contribution = spotDirect.color * lightAmount;
        irradiance += contribution;
        dominant += spotDirect.direction * lum( contribution );
      }
      #pragma unroll_loop_end
    #endif

    #if NUM_HEMI_LIGHTS > 0
      #pragma unroll_loop_start
      for ( int i = 0; i < NUM_HEMI_LIGHTS; i ++ ) {
        irradiance += getHemisphereLightIrradiance( hemisphereLights[ i ], N );
      }
      #pragma unroll_loop_end
    #endif

    irradiance += ambientLightColor;

    float energy = lum( irradiance );
    float t = clamp( energy / max( uExposureRef, 1e-4 ), 0.0, 1.0 );

    // The hue of the light actually arriving, separated from its strength. The
    // cull corridor's one red lamp has to tint what it reaches even though the
    // BAND VALUES are authored per zone and never move.
    vec3 lightHue = energy > 1e-3 ? irradiance / energy : vec3( 1.0 );

    float b1 = smoothstep( uThresholds.x - uSoftness.x, uThresholds.x + uSoftness.x, t );
    float b2 = smoothstep( uThresholds.y - uSoftness.y, uThresholds.y + uSoftness.y, t );

    // Three LIGHT COLOURS, not three brightnesses. The shadow band is the zone's
    // fill hue — warm bounce — and never a darkened key and never neutral grey.
    vec3 bandShadow = mix( uShadowCol, uFill, uShadowFillMix );
    vec3 bandHigh = uKey * lightHue;
    bandHigh = mix( bandHigh, vec3( lum( bandHigh ) ), uHighlightDesat );
    vec3 bandMid = mix( bandShadow, bandHigh, 0.55 );

    vec3 light = mix( bandShadow, bandMid, b1 );
    light = mix( light, bandHigh, b2 );

    vec3 albedo = uDiffuse;

    // Wear (D-002). Monotonic across the game, applied only to fabric, and it
    // desaturates as well as dirties — a toy that has been through a slurry
    // channel loses its colour before it loses its shape.
    albedo = mix( albedo, mix( albedo, uWearTint, 0.55 ) * 0.92, uWear * uWearable );

    // ── The room's grime ─────────────────────────────────────────────────────
    //
    // Three effects off one noise field, and the reason they are here rather
    // than in hand-placed decals is the same argument D-003 makes about her
    // fabric: detail that has to follow geometry belongs in the shader, because
    // geometry gets rebuilt and decals do not follow it.
    //
    // Gated on ( 1 - uWearable ), which reads as "everything except her".
    // Fabric is hers alone and it already has D-002's wear channel; a room that
    // could dirty her would take the story's one monotonic device away from the
    // story and give it to the level. That gate is the whole reason wearable was
    // worth having as a per-kind parameter rather than a hard-coded test on the
    // surface kind.
    float wetSheen = 0.0;
    if ( uGrime > 0.0 && uWearable < 0.5 ) {
      vec3 WN = normalize( vWorldNormal );
      if ( ! gl_FrontFacing ) WN = -WN;

      // Two octaves. The coarse one is where the slurry ran; the fine one is
      // the speckle that stops the coarse one reading as an airbrush.
      float coarse = grimeNoise( vWorldPos * 2.6 );
      float fine = grimeNoise( vWorldPos * 12.0 );
      float n = coarse * 0.68 + fine * 0.32;

      // 1. Slurry climbing anything vertical. Height above the floor is the
      //    whole driver: a hosed-out farrowing house is filthy to about knee
      //    height and clean above it, because that is as far as a jet reaches
      //    and as high as a sow can reach to foul.
      float low = 1.0 - smoothstep( 0.04, 0.5, vWorldPos.y );
      float vertical = 1.0 - abs( WN.y );
      float stain = low * vertical * smoothstep( 0.3, 0.78, n );

      // 2. Standing water on horizontals. Large, soft patches — a floor with a
      //    fall to a drain is never uniformly wet and never uniformly dry.
      float up = smoothstep( 0.5, 0.92, WN.y );
      wetSheen = up * smoothstep( 0.4, 0.8, grimeNoise( vWorldPos * 0.85 + 31.0 ) );

      // 3. Rust, on galvanized steel only, where water sits and where a
      //    fitting meets the floor. The zone already carried a rust value that
      //    nothing used; this is what it is for.
      float rust = uRustable * smoothstep( 0.52, 0.9, fine )
        * ( 0.35 + 0.65 * low ) * ( 0.4 + 0.6 * up );

      float dirt = clamp( ( stain + wetSheen * 0.55 ) * uGrime, 0.0, 1.0 );
      albedo = mix( albedo, uGrimeColour, dirt * 0.8 );
      albedo = mix( albedo, uRustColour, clamp( rust * uGrime, 0.0, 1.0 ) * 0.7 );

      // Wet concrete is DARKER as well as shinier, and the darkening is the
      // half people forget. A sheen alone on unchanged albedo reads as polish.
      albedo *= 1.0 - 0.22 * wetSheen * uGrime;
      wetSheen *= uGrime;
    }

    // Felt seam. Object space; see SurfaceOptions.seam.
    if ( uSeamWidth > 0.0 ) {
      float across = abs( vObjectPos.x );
      // Only on the upper surface: a seam is sewn along the top, and without
      // this gate it wraps under the belly and reads as a crack.
      float upper = smoothstep( 0.05, 0.45, normalize( vObjectNormal ).y );
      float seam = ( 1.0 - smoothstep( uSeamWidth * 0.55, uSeamWidth, across ) ) * upper;
      // Stitch ticks along the seam's length. Period is deliberately coarse —
      // fine stitching disappears at gameplay distance and only costs fill rate.
      float tick = 0.55 + 0.45 * sin( vObjectPos.z * 220.0 );
      albedo = mix( albedo, uSeamColour, seam * tick * 0.85 );
    }

    vec3 col = albedo * light;

    // Pile scatter at glancing angles, gated on b1 so it reaches ZERO in shadow.
    //
    // The gate was ( 0.2 + 0.8 * b1 ), which floors at a fifth rather than at
    // nothing — so her silhouette kept a faint rim in full shadow while the
    // comment above it claimed it could not. Caught on independent review, and
    // it is the exact failure mode this project keeps hitting: a confident
    // comment next to code that does something else. A rim that glows in the
    // dark is a video-game effect and it would undo everything the wrap term is
    // for.
    if ( uFuzz > 0.0 ) {
      float rim = pow( 1.0 - clamp( dot( N, V ), 0.0, 1.0 ), 3.0 );
      col += uFuzz * rim * albedo * bandHigh * b1;
    }

    // Toon specular: a blob with a hard-ish edge, not a lobe.
    if ( uGlint > 0.0 && length( dominant ) > 1e-4 ) {
      vec3 R = reflect( -normalize( dominant ), N );
      float sp = smoothstep( 0.87, 0.93, dot( R, V ) );
      col += uGlint * sp * bandHigh * b2;
    }

    // A wet floor catching the battens, and it is deliberately NOT the metal
    // glint with a different number in front of it. The glint above is a hard
    // blob because a soft highlight in a banded image reads as a rendering
    // mistake — but that argument is about a small curved fitting. On a twenty
    // metre floor the same blob is a bright coin sliding around under the camera.
    // Standing water scatters, so this is wide and low, and it is gated on b1
    // rather than b2 so a wet patch between two battens still catches something.
    if ( wetSheen > 0.0 && length( dominant ) > 1e-4 ) {
      vec3 R = reflect( -normalize( dominant ), N );
      float sp = smoothstep( 0.5, 0.98, dot( R, V ) );
      col += wetSheen * 0.42 * sp * bandHigh * b1;
    }

    // Self-lit (SurfaceOptions.glow): after the bands, before the haze, so a
    // lit room far down a corridor still fades into it.
    col += albedo * uGlow;

    // ── Haze, and why it is not three's fog ──────────────────────────────────
    //
    // Three uploads its fogColor uniform in a colour space that depends on
    // WHERE YOU ARE RENDERING (WebGLMaterials.js, via getUnlitUniformColorSpace):
    // the output colour space when drawing to the canvas, the linear working
    // space when drawing to a render target. That is correct for three's own
    // materials, which mix fog AFTER the sRGB encode.
    //
    // This material cannot mix after the encode, because the post stack needs a
    // linear beauty buffer. Using three's fog anyway produced a bug that took a
    // long time to corner: **the same scene rendered noticeably hazier straight
    // to the canvas than into the post stack's target**, with no shader
    // difference at all — the fog colour was arriving already-encoded in one
    // path and linear in the other, a factor of 2.5 in the value.
    //
    // So the haze is ours. One uniform, one meaning, in every path.
    float fogDepth = length( vViewPos );
    float fogFactor = 1.0 - exp( - uFogDensity * uFogDensity * fogDepth * fogDepth );
    col = mix( col, uFogColour, clamp( fogFactor, 0.0, 1.0 ) );

    gl_FragColor = vec4( col, 1.0 );

    #include <colorspace_fragment>
  }
`;

/**
 * A painted surface.
 *
 * `colour` is the albedo in sRGB hex, as every palette value in this project is.
 * The LIGHT is the zone's; the albedo is the object's. Keeping those two apart
 * is what lets one kit tile be placed in five zones and belong in each.
 */
export function toonMaterial(
  kind: SurfaceKind,
  colour: number,
  options: SurfaceOptions = {},
): THREE.ShaderMaterial {
  const params = SURFACE[kind];
  const seamWidth = options.seam === true ? (options.seamWidth ?? DEFAULT_SEAM_WIDTH) : 0;
  const seamColour = options.seamColour ?? ROSALIE.seam;
  const glow = Math.max(0, options.glow ?? 0);

  const material = new THREE.ShaderMaterial({
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    lights: true,
    // `fog: false` deliberately. The haze is this shader's own — see the note in
    // the fragment source about three uploading its fog colour in a colour space
    // that depends on the render target.
    fog: false,
    uniforms: THREE.UniformsUtils.merge([
      THREE.UniformsLib['lights'],
      {
        uDiffuse: { value: new THREE.Color(colour) },
        uWrap: { value: params.wrap },
        uFuzz: { value: params.fuzz },
        uGlint: { value: params.glint },
        uWearable: { value: params.wearable },
        uRustable: { value: params.rustable },
        uSeamWidth: { value: seamWidth },
        uSeamColour: { value: new THREE.Color(seamColour) },
        uGlow: { value: glow },
      },
    ]),
  });

  // Merged AFTER the clone that `UniformsUtils.merge` performs, on purpose.
  //
  // `merge` deep-clones everything it is given, which is exactly what we want
  // for the per-material values above and exactly what we must NOT have for the
  // zone state — a cloned uniform object would silently detach that material
  // from `applyZone()` and it would keep the palette it was born with. That
  // failure is invisible in a screenshot of a single object and obvious only
  // once a zone changes, which is the worst possible time to find it.
  Object.assign(material.uniforms, zoneUniforms);

  // A stable identity for "these two materials are the same surface".
  //
  // Asset factories are pure and construct their own materials, so two kit
  // pieces painted identically still produce two objects — and a batcher that
  // groups by object identity cannot merge them. The farrowing crate built one
  // material per pipe and cost 106 draw calls on its own because of exactly
  // that. See src/world/instancing.ts.
  // Seam colour is part of the identity, not a detail. Two materials that differ
  // only in thread colour are NOT the same surface, and merging them by a key
  // that ignores it would repaint her mother's strained seams in Rosalie's.
  // Glow joins the key only when it is set, so every existing surface keeps
  // the key it had (`asset-contract.ts` hashes it).
  material.userData['surfaceKey'] =
    `${kind}:${colour.toString(16)}:${seamWidth}:${seamColour.toString(16)}${glow > 0 ? `:glow${String(glow)}` : ''}`;

  live.add(material);
  const dispose = material.dispose.bind(material);
  material.dispose = (): void => {
    live.delete(material);
    dispose();
  };

  return material;
}

/**
 * Point every toon material in the game at a zone's palette.
 *
 * One assignment per uniform, because they are shared objects. See zoneUniforms.
 */
export function applyZone(zone: ZonePalette, fogDensity = 0): void {
  zoneUniforms.uKey.value.set(zone.key);
  zoneUniforms.uFill.value.set(zone.fill);
  zoneUniforms.uShadowCol.value.set(zone.shadow);
  zoneUniforms.uFogColour.value.set(zone.fog);
  // Density is a parameter with a default of ZERO rather than something a caller
  // may forget to set. It used to be a separate setter that only the lighting
  // rig called, which meant applying the neutral studio zone left the previous
  // room's haze in place — invisible today, because every call site runs once
  // per page load, and a real bug the first time anything switches zones without
  // a reload. Found on independent review.
  zoneUniforms.uFogDensity.value = Math.max(0, fogDensity);

  // Grime comes from the zone, and a zone WITHOUT it must clear it rather than
  // inherit the last room's — the same trap `fogDensity` above records having
  // fallen into. `grime` is optional precisely because the studio fixture and the
  // outside zones are not soiled, so the absent case is the common one and has to
  // be the correct one.
  zoneUniforms.uGrime.value = zone.grime === undefined ? 0 : GRIME_STRENGTH;
  zoneUniforms.uGrimeColour.value.set(zone.grime ?? 0x000000);
  // The rust the zone already carried and nothing used: `surfaces[3]` is the
  // rust entry in every zone's list (see palette.ts). Falling back to the grime
  // colour rather than to a hex literal here keeps a zone that adds `grime`
  // without a fourth surface from bleeding black.
  zoneUniforms.uRustColour.value.set(zone.surfaces[3] ?? zone.grime ?? 0x000000);
}

/**
 * How dirty a soiled zone is. One number, not per zone, on purpose.
 *
 * Per-zone strengths would be five values to keep in agreement with each other,
 * and the thing that actually differs between a farrowing house and a fattening
 * hall is the *colour* of the filth, which is what `ZonePalette.grime` is. If a
 * zone ever genuinely needs to be dirtier rather than differently dirty, promote
 * this — but do it in palette.ts, where the mood board can see it, and not here.
 *
 * 0.85 rather than 1.0 because at full strength the noise floor stops reading as
 * dirt and starts reading as a texture problem: every surface acquires a speckle
 * and the room loses the clean-above-knee-height line that makes the staining
 * legible as hosing in the first place.
 */
const GRIME_STRENGTH = 0.85;

/**
 * Override the room's grime, 0..1. Test and workbench seam.
 *
 * The game does not call this — `applyZone` owns the value, because grime is a
 * property of the zone. It exists so a before/after screenshot can be taken from
 * the same build, which is the only way spec §0's before/after evidence
 * requirement can be met for a shader change.
 */
export function setGrime(grime: number): void {
  zoneUniforms.uGrime.value = THREE.MathUtils.clamp(grime, 0, 1);
}

/** Current room grime, for the workbench readout. */
export function getGrime(): number {
  return zoneUniforms.uGrime.value;
}


/**
 * The irradiance that counts as fully lit. Set by the lighting rig, so adding a
 * fixture brightens the room without moving where the bands fall.
 */
export function setExposureReference(value: number): void {
  zoneUniforms.uExposureRef.value = value;
}

/**
 * The exposure reference the shader is using. A read-back, for the bloom's gate.
 *
 * Same reason `PostStack.chromaCeiling` exists: a census that reports its own
 * arithmetic cannot tell you whether the setter was called.
 */
export function getExposureReference(): number {
  return zoneUniforms.uExposureRef.value;
}

/** D-002's wear, 0..1. Monotonic across the game and never reset. */
export function setWear(wear: number): void {
  zoneUniforms.uWear.value = THREE.MathUtils.clamp(wear, 0, 1);
}

/** Current wear, so a save can round-trip it. */
export function getWear(): number {
  return zoneUniforms.uWear.value;
}

/** How many toon materials are alive. A leak check for the workbench. */
export function liveMaterialCount(): number {
  return live.size;
}
